import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  LinkOff as LinkOffIcon,
  MoreVert as MoreIcon,
  SwapHoriz as RebindIcon,
} from '@mui/icons-material';
import MsIssueDetails from './MsIssueDetails';
import {
  MODE_SOURCE_LABEL,
  barcodeMatches,
  effectiveMode,
  forcedForAccount,
  linkBarcodes,
  linkSkuLabel,
  severityColor,
  sortIssues,
  stockModeChipProps,
  stockModeLabel,
} from './msProductsModel';

const SKU_LABEL = { wb: 'nmID', ozon: 'SKU', yandex: 'marketSku' };

export default function MsLinkRow({
  link,
  product,
  labels,
  highlighted,
  actions,
  busy,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openCodes, setOpenCodes] = useState(() => new Set());
  const issues = sortIssues(link.issues);
  const barcodes = linkBarcodes(link);
  const match = barcodeMatches(product?.barcodes, barcodes);
  const forced = forcedForAccount(product?.forced, link.account_id);
  const sku = linkSkuLabel(link);
  const title = link.article || link.card_article || link.product_key;
  const name = link.mp_name || link.card_name;
  const rebind = issues.find((i) => i.code === 'REBIND_AVAILABLE' && i.details?.target_ms);

  const toggleCode = (code) => setOpenCodes((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    return next;
  });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: highlighted ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1.25,
        bgcolor: highlighted ? 'action.selected' : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={500} noWrap title={name ? `${title} · ${name}` : title}>
            {title}
          </Typography>
          {name && (
            <Typography variant="caption" color="text.secondary" noWrap component="div" title={name}>{name}</Typography>
          )}
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }} alignItems="center">
            {sku && (
              <Typography variant="caption" color="text.secondary">
                {SKU_LABEL[link.marketplace] || 'SKU'}: <Box component="span" sx={{ fontFamily: 'monospace' }}>{sku}</Box>
              </Typography>
            )}
            {String(link.product_key) !== sku && (
              <Typography variant="caption" color="text.secondary">
                ключ: <Box component="span" sx={{ fontFamily: 'monospace' }}>{link.product_key}</Box>
              </Typography>
            )}
            {barcodes.length > 0 && (
              <Tooltip title={match == null ? '' : (match ? 'ШК совпадает со ШК товара МС' : 'ШК не совпадает со ШК товара МС')}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace' }}
                  color={match === false ? 'warning.main' : match ? 'success.main' : 'text.secondary'}
                >
                  ШК {barcodes.join(' / ')}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Tooltip title="Трансляция остатков на МП. Заказы, подбор и перемещения идут по связи независимо от неё.">
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">трансляция</Typography>
            <Switch
              size="small"
              checked={!!link.enabled}
              disabled={busy}
              onChange={(e) => actions.setLinkEnabled(link, e.target.checked)}
              inputProps={{ 'aria-label': 'Трансляция связи' }}
            />
          </Stack>
        </Tooltip>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Действия со связью">
          <MoreIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">Остаток по складам:</Typography>
        {forced.length ? forced.map((f) => {
          const eff = effectiveMode(f);
          return (
            <Tooltip key={f.binding_id} title={`${MODE_SOURCE_LABEL[eff.source] || ''}${eff.source === 'binding' ? ` (${f.binding_name || f.binding_id})` : ''}`}>
              <Chip
                size="small"
                {...stockModeChipProps(eff)}
                label={`${f.binding_name || f.binding_id}: ${stockModeLabel(eff)}`}
                sx={{ height: 20, fontSize: 11, opacity: eff.mode === 'none' ? 0.75 : 1 }}
              />
            </Tooltip>
          );
        }) : (
          <Typography variant="caption" color="text.disabled">нет складских связок</Typography>
        )}
        <Tooltip title="Режим остатка по складам: живой / принуд / не транслировать">
          <IconButton size="small" onClick={() => actions.openForced()} sx={{ p: 0.25 }} aria-label="Остаток по складам">
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {issues.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {issues.map((issue) => {
              const open = openCodes.has(issue.code);
              return (
                <Chip
                  key={issue.code}
                  size="small"
                  color={severityColor(issue.severity)}
                  variant={open ? 'filled' : 'outlined'}
                  label={labels?.[issue.code]?.label || issue.code}
                  onClick={() => toggleCode(issue.code)}
                  onDelete={() => toggleCode(issue.code)}
                  deleteIcon={open ? <ExpandLess /> : <ExpandMore />}
                  sx={{ height: 22, fontSize: 12 }}
                />
              );
            })}
          </Stack>
          {issues.map((issue) => (
            <Collapse key={issue.code} in={openCodes.has(issue.code)} unmountOnExit>
              <MsIssueDetails issue={issue} labels={labels} link={link} product={product} actions={actions} busy={busy} />
            </Collapse>
          ))}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            if (rebind) actions.openRebind(link, rebind.details.target_ms);
            else actions.openLinkDialog({ marketplace: link.marketplace, account_id: link.account_id });
          }}
        >
          <ListItemIcon><RebindIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={rebind ? `Перепривязать на «${rebind.details.target_ms.name || rebind.details.target_ms.ms_id}»` : 'Привязать другую карточку'} />
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); actions.removeLink(link); }}>
          <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Удалить связь" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
